/* eslint-disable max-len */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import ModalConfirmation from 'components/ModalConfirmation';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getTiposDePlazas,
  postTipoDePlaza,
  deleteTipoDePlaza,
  patchTipoDePlaza,
  getTipoDePlaza,
} from 'api/nominas/catalogos/tiposDePlazas';
import { checkToken } from 'api/utils';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_INACTIVO,
} from 'utils/constants';

export const tiposDePlazasPermission = {
  permissionModel: 'tipodeplaza',
  catalogsModels: [],
  tabsModels: [],
};

const TiposDePlazas = ({ permission }) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [confirmationVisibleEstatus, setConfirmationVisibleEstatus] = useState(false);
  const [estatusInactivo, setEstatusInactivo] = useState(false);
  const [estatusAutorizado, setEstatusAutorizado] = useState(false);
  const [estadosFiltered, setEstadosFiltered] = useState([]);
  const [disableAuth, setDisableAuth] = useState(false);

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getTiposDePlazas();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getTiposDePlazas(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setEstatusInactivo(false);
    setEstatusAutorizado(false);
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _plaza = await getTipoDePlaza(match.id);
    if (_plaza?.estados_globales === ESTATUS_ACTIVO
      || _plaza?.estados_globales === ESTATUS_INACTIVO) {
      setEstadosFiltered(estados.filter((estado) => estado?.id === ESTATUS_INACTIVO
      || estado?.id === ESTATUS_ACTIVO));
    }
    if (_plaza?.estados_globales === ESTATUS_AUTORIZADO) {
      setEstadosFiltered(estados.filter((estado) => estado?.id === ESTATUS_AUTORIZADO));
      setEstatusAutorizado(true);
    }
    if (_plaza?.estados_globales === ESTATUS_INACTIVO) {
      setEstatusInactivo(true);
    }
    setTimeout(() => {
      if (_plaza) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_plaza));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchTipoDePlaza(key, values) : postTipoDePlaza(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteTipoDePlaza(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    const isAutorizado = record?.estados_globales === ESTATUS_AUTORIZADO;
    const isInactivo = record?.estados_globales === ESTATUS_INACTIVO;
    setDisableAuth(isAutorizado || isInactivo);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    setEstadosFiltered(estados);
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar una nómina ya autorizada');
        return;
      }
      const vals = {
        ...form.getFieldValue(),
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchTipoDePlaza(key, vals);
      if (res) {
        onSuccess(res);
        await fetchData();
        setConfirmationVisible(false);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishConfirmationEstatus = async () => {
    setConfirmationVisibleEstatus(false);
  };

  const columns = [
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 400,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          disableDelete={
            data?.find((e) => e?.id === selectedRowKeys[0])?.estados_globales === ESTATUS_AUTORIZADO
          }
          customActions={[
            {
              onClick: () => setConfirmationVisible(true),
              permission: { model: 'tipodeplaza', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length || disableAuth,
            },
          ]}
          permission={permission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          keyboard={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tipo de Plaza`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!selectedRowKeys?.length || !disableAuth}
            />
          )}
          onCancel={onCancel}
          forceRender
          width="35%"
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="descripcion"
                rules={rules.required}
                label="Descripción"
                hasFeedback
              >
                <Input.TextArea
                  allowClear
                  autoSize={{ minRows: 2, maxRows: 3 }}
                  disabled={estatusAutorizado || estatusInactivo}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.estatus}
                hasFeedback
                initialValue={1}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estadosFiltered}
                  disabled={!selectedRowKeys.length || estatusAutorizado || estatusInactivo}
                  onChange={(val) => {
                    if (val === ESTATUS_INACTIVO) {
                      setConfirmationVisibleEstatus(true);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Tipo de Plaza ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => {
            setConfirmationVisible(false);
          }}
          open={confirmationVisible}
          contentTitle={`Autorizar el Tipo de Plaza  ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmationEstatus}
          ConfirmInactive
          onCancel={() => {
            setConfirmationVisibleEstatus(false);
            form.setFieldValue('estados_globales', 1);
          }}
          open={confirmationVisibleEstatus}
          contentTitle={`Inactivar el Tipo de Plaza ${form.getFieldValue('descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default TiposDePlazas;
